import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import TextField from "@components/ui/textfields/TextField";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";
import { vehiclesApi } from "@services/api/vehicles";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import type { OrdreStatus } from "types/ordre";

function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + "T12:00:00");
  return Math.floor(d.getTime() / 1000);
}

function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Étapes d'une location seule, portées par son ordre TEMP : c'est le statut de l'ordre
// qui colore le magnet au planning, comme pour une réception.
const LOCATION_STATUS_OPTIONS = [
  { value: "rdv", label: "Prévue" },
  { value: "reception", label: "Réceptionnée" },
  { value: "restitue", label: "Restituée" },
];

interface LocationModalProps {
  reservation: VehicleReservation;
  onClose: () => void;
  onSave?: (updated: VehicleReservation) => void;
  /** Appelé une fois la location supprimée — au planning de se rafraîchir. */
  onDeleted?: () => void;
}

export default function LocationModal({
  reservation,
  onClose,
  onSave,
  onDeleted,
}: LocationModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const canDelete = isOwnerOrAdmin(userData);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [form, setForm] = useState({ ...reservation });
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [ordreStatus, setOrdreStatus] = useState<string | null>(null);
  const [savedOrdreStatus, setSavedOrdreStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!reservation.ordreId) return;
    let alive = true;
    ordresApi
      .get(reservation.ordreId)
      .then((o) => {
        if (!alive) return;
        setOrdreStatus(o.status);
        setSavedOrdreStatus(o.status);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [reservation.ordreId]);

  const isMultiDay = form.startDate !== form.endDate;
  const endHour =
    form.returnHour ??
    (form.startHour != null && form.durationHours != null
      ? form.startHour + form.durationHours
      : undefined);

  // Véhicules LIBRES sur la période, pas toute la flotte de location : la liste
  // proposait des voitures déjà sorties. La location en cours de modification s'exclut
  // elle-même de l'occupation (`exclude_reservation_id`), sinon son propre véhicule
  // disparaîtrait des choix.
  useEffect(() => {
    if (!orgData?.id || !form.startDate || !form.endDate) return;
    let alive = true;
    vehiclesApi
      .available({
        org_id: orgData.id,
        usage: "location",
        start_date: form.startDate,
        end_date: form.endDate,
        start_hour: form.startHour ?? undefined,
        end_hour: endHour,
        all_categories: true,
        exclude_reservation_id: reservation.id,
      })
      .then((list) => { if (alive) setVehicles(list); })
      .catch(() => {});
    return () => { alive = false; };
  }, [orgData?.id, reservation.id, form.startDate, form.endDate, form.startHour, endHour]);

  const update = <K extends keyof VehicleReservation>(key: K, value: VehicleReservation[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const vehicleOptions = vehicles.map((v) => ({
    value: v.id,
    label: `${v.name} — ${v.plate}`,
  }));

  const endTimeStr =
    !isMultiDay && form.startHour != null && form.durationHours != null
      ? hourToTimeStr(form.startHour + form.durationHours)
      : "";

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await vehiclesApi.updateReservation(reservation.id, {
        clientName: form.clientName,
        vehicleId: form.vehicleId,
        startDate: form.startDate,
        endDate: form.endDate,
        startHour: form.startHour,
        durationHours: form.durationHours,
        returnHour: form.returnHour,
        clientEmail: form.clientEmail,
        clientPhone: form.clientPhone,
        notes: form.notes,
      });
      if (reservation.ordreId && ordreStatus && ordreStatus !== savedOrdreStatus) {
        await ordresApi.update(reservation.ordreId, { status: ordreStatus as OrdreStatus });
      }
      onSave?.(updated);
      showNotification({ message: "Location mise à jour", type: "success" });
      onClose();
    } catch (e: any) {
      showNotification({
        message: e?.response?.status === 409
          ? e.response.data?.detail || "Modification refusée"
          : "Erreur lors de la sauvegarde",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // La suppression emporte aussi les magnets du planning (cascade côté back), sinon
  // le bloc restait accroché à une réservation disparue.
  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await vehiclesApi.deleteReservation(reservation.id);
      showNotification({ message: "Location supprimée", type: "success" });
      setShowDeleteConfirm(false);
      onDeleted?.();
      onClose();
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
      setDeleting(false);
    }
  };

  return (
    <>
    <BaseModal
      title="Location"
      startIcon={<Icon1 name="car-reception" size={20} />}
      onClose={onClose}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {form.dossierId && (
          <Link
            to={`/dossier/${form.dossierId}`}
            onClick={onClose}
            className="self-start"
          >
            <SecondaryButton variant="small" startIcon={<Icon1 name="folder" size={14} />}>
              Voir le dossier
            </SecondaryButton>
          </Link>
        )}

        <TextField
          label="Client"
          value={form.clientName}
          onChange={(v) => update("clientName", v)}
        />

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Email"
              value={form.clientEmail || ""}
              onChange={(v) => update("clientEmail", v)}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="Téléphone"
              value={form.clientPhone || ""}
              onChange={(v) => update("clientPhone", v)}
            />
          </div>
        </div>

        <DropDownSearch
          label="Véhicule de location"
          options={vehicleOptions}
          value={form.vehicleId}
          onSelect={(v) => update("vehicleId", v as string)}
        />
        <span className="text-h4 text-neutral-400 -mt-1">
          Seuls les véhicules libres sur la période sont proposés.
        </span>

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DatePicker
              label="Date début"
              seconds={isoToSeconds(form.startDate)}
              onChange={(s) => update("startDate", secondsToIso(s))}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label="Date fin"
              seconds={isoToSeconds(form.endDate)}
              onChange={(s) => update("endDate", secondsToIso(s))}
              minDateSeconds={isoToSeconds(form.startDate)}
            />
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TimePicker
              label="Heure de départ"
              value={form.startHour != null ? hourToTimeStr(form.startHour) : null}
              onChange={(timeStr) => {
                if (!timeStr) return;
                const newStart = timeStrToHour(timeStr);
                if (!isMultiDay && form.startHour != null && form.durationHours != null) {
                  const endHour = form.startHour + form.durationHours;
                  update("durationHours", Math.max(0.25, endHour - newStart));
                }
                update("startHour", newStart);
              }}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Heure de fin"
              value={
                isMultiDay
                  ? form.returnHour != null
                    ? hourToTimeStr(form.returnHour)
                    : null
                  : endTimeStr || null
              }
              onChange={(timeStr) => {
                if (!timeStr) return;
                const endHour = timeStrToHour(timeStr);
                if (isMultiDay) {
                  update("returnHour", endHour);
                } else {
                  const start = form.startHour ?? 8;
                  update("durationHours", Math.max(0.25, endHour - start));
                }
              }}
            />
          </div>
        </div>

        {ordreStatus != null && (
          <DropDown
            label="Statut"
            options={
              LOCATION_STATUS_OPTIONS.some((o) => o.value === ordreStatus)
                ? LOCATION_STATUS_OPTIONS
                : [...LOCATION_STATUS_OPTIONS, { value: ordreStatus, label: ordreStatus }]
            }
            value={ordreStatus}
            onSelect={(v) => setOrdreStatus(String(v))}
          />
        )}

        <TextField
          label="Notes"
          value={form.notes || ""}
          onChange={(v) => update("notes", v)}
        />
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-between gap-2">
        {canDelete ? (
          <SecondaryButton variant="small" danger onClick={() => setShowDeleteConfirm(true)}>
            Supprimer
          </SecondaryButton>
        ) : (
          <span />
        )}
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button variant="small" label="Enregistrer" onClick={handleSave} disabled={saving} />
        </div>
      </div>
    </BaseModal>

    {showDeleteConfirm && (
      <ConfirmationModal
        title="Supprimer cette location ?"
        message="Cette action est irréversible. La location et ses rendez-vous de départ et de retour seront définitivement supprimés."
        confirmLabel={deleting ? "Suppression..." : "Oui, supprimer"}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
      />
    )}
    </>
  );
}
